import { useEffect, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { businessAnalyticsApi } from "../../services/businessApi";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Spinner } from "../../components/common/Spinner";
import { LockedFeature } from "../../components/common/LockedFeature";
import { DataTable } from "../../components/common/DataTable";
import { money } from "../../utils/format";

/*
 * INVOICED AND RECEIVED ARE THE SAME MEASURE AT TWO STAGES (#431), so
 * they are two steps of one hue rather than two unrelated colours.
 * Expenses is a different thing and gets a different hue.
 *
 * Validated rather than eyeballed. The obvious pairing -- the old green
 * income bar beside the red expense bar -- separates by only dE 6.3
 * under deuteranopia, which is the classic red/green failure and was
 * shipped here. These three separate by dE 15.9 at worst across ALL
 * pairs, not just adjacent ones.
 *
 * The dark surface leaves #1a5fa8 at 2.69:1, under the 3:1 bar. That is
 * discharged by the table below carrying every number in text -- which
 * is the required relief, and was being added anyway.
 */
const INVOICED = "#4a90d9";
const RECEIVED = "#1a5fa8";
const EXPENSES = "#c0492b";

export function PnLPage() {
  const { hasFeature } = useFeatureTheme();
  const [pnl, setPnl] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  /* BREAK-EVEN LIVES HERE NOW (#453). It had its own tab, and it stopped
     deserving one: #432 established that app.BreakEvenAnalysis can never
     have rows -- nothing in the schema attributes revenue to a piece of
     equipment -- so the page became four figures and a sentence.

     A tab whose entire content is an aggregate of the tab beside it is
     the #402 shape, "a tab is a question, and two screens were asking
     the same one". Reported from testing as "it just feels thin for
     it's own tab", which is the same observation from the other side. */
  const [breakEven, setBreakEven] = useState(null);
  // The owner has hidden money from this person (#472). Distinct
  // from "loading" and from "no data", and it is neither.
  const [denied, setDenied] = useState(false);

  /* INVOICED, DRAWN AS THE SPACE ABOVE RECEIVED (#452). Asked for as
     "maybe an unfilled bar showing invoiced above the 'Income
     received'", which is better than the third grouped bar it replaced
     the idea of: invoiced = received + outstanding, so stacking the two
     gives a bar whose FULL HEIGHT is invoiced and whose filled part is
     the money that actually arrived. The gap between them becomes a
     quantity you can see rather than a figure in the table.

     CLAMPED AT ZERO, and that is not tidiness. Outstanding goes NEGATIVE
     when payments land against an earlier season's invoices -- ordinary
     for consignment (#226) -- and a negative stacked segment renders
     BELOW the baseline, which would read as a loss. The table still
     reports the real signed figure; the chart declines to draw a
     quantity it cannot draw honestly. */
  const chart = (pnl || []).map((r) => ({
    ...r,
    unpaid: Math.max(0, Number(r.outstanding) || 0),
  }));

  /* EVERY ONE OF THESE NEEDS A CATCH (#472).
     
     Without one, a 403 rejects unhandled, `pnl` stays null, and the
     cards sit on their loading state forever. An infinite spinner is
     the worst terminal state available: it says "working" indefinitely,
     so the user waits, reloads, waits again, and reports the app as
     broken. A refusal is information; a spinner is not.
     
     Reported as "all the cards are stuck at the spinning circle". */
  useEffect(() => {
    businessAnalyticsApi.pnl(year)
      .then((r) => { setPnl(r.pnl); setDenied(false); })
      .catch((err) => {
        /* BRANCH ON THE REASON, NOT THE STATUS.
         *
         * This read `if (err.response?.status === 403) setDenied(true)`,
         * with a comment asserting "403 here means the owner has not
         * given this person access to money". That is one of TWO
         * refusals this endpoint makes: it is declared
         * `@secure_endpoint(feature="BusinessAdvanced", financials=True)`,
         * so it also 403s when the ACCOUNT's tier does not include the
         * feature at all.
         *
         * Reported from the web round: a helper viewing P&L on an
         * account they support, who switches "Working in" back to their
         * own Free apiary, was told "Money is hidden on this account --
         * ask the owner to turn on can see money". They ARE the owner.
         * No tick box exists for it and no conversation fixes it; the
         * answer is a different plan. And because `denied` renders
         * before the tier lock, the correct upgrade view was shadowed.
         *
         * The server has always said which it was -- error_response
         * sends `{error: <code>}`, and apiClient normalises it onto
         * `apiErrorCode`. `feature_disabled` is the tier boundary;
         * `forbidden` is the money refusal. Same precedent as
         * JITAccessRequiredError, which exists purely so the admin app
         * can route a 403 by its code rather than its status.
         *
         * So: fall through on a tier refusal and let LockedFeature do
         * its job, which is also what somebody sees if they click here
         * directly. Set `denied` only for the money refusal, where
         * upgrading genuinely changes nothing. */
        /* Read from BOTH places on purpose. apiClient normalises the
           code onto `apiErrorCode`, and that is the field to prefer --
           but a caller that constructs an error without going through
           the interceptor has only `response.data.error`, and a rule
           that silently depends on which it got is the mock-divergence
           trap this suite has been bitten by before. Same answer either
           way. */
        if (err.response?.status === 403) {
          const code = err.apiErrorCode ?? err.response?.data?.error;
          setDenied(code !== "feature_disabled");
        }
        setPnl([]);
      });
  }, [year]);

  /* FETCHED ONCE, AND DELIBERATELY NOT IN THE YEAR EFFECT. Break-even is
     ALL TIME: "has the operation paid for itself" is not a question about
     one season, and re-running it when the year selector moves would make
     it look like one. The endpoint ignores the year for the same reason. */
  useEffect(() => {
    businessAnalyticsApi.breakEven()
      .then((r) => setBreakEven(r.break_even))
      // Same gate, same refusal. Swallowed rather than surfaced twice:
      // the P&L fetch above already says why the page is empty, and two
      // explanations of one cause read as two problems.
      .catch(() => setBreakEven(null));
  }, []);

  return (
    <div>
      <PageHeader title="Profit & Loss by Season">
        <input id="pn-lpage-year" name="year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="touch-target" style={{ width: 100 }} />
      </PageHeader>
      {/* BEFORE the tier lock, because it is the more specific answer.
          Somebody refused for money access would otherwise be told to
          upgrade -- which is both wrong and unhelpful, since no plan
          grants this. The account owner does, with a tick box. */}
      {denied ? (
        <Card title="Money is hidden on this account">
          <p style={{ margin: 0, maxWidth: "60ch" }}>
            The owner of this apiary has not given you access to financial
            information, so profit, loss and break-even are not shown here.
          </p>
          <p style={{ margin: "var(--space-2) 0 0", maxWidth: "60ch",
                      color: "var(--color-text-muted)" }}>
            Everything else on the account works as usual. If you need these
            figures, ask the owner to turn on &ldquo;can see money&rdquo; for
            you in their team settings.
          </p>
        </Card>
      ) : (
      <LockedFeature enabled={hasFeature("BusinessAdvanced")} featureLabel="Business Advanced" previewText="See income, expenses, and profit broken down by season.">
        {/* FIRST, because it is the shortest answer on the page and the
            one somebody arrives wanting. The charts are how it got there
            and the table is the working. */}
        {breakEven && (
          <Card title="Has it paid for itself?" className="section-spacing">
            <div style={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: breakEven.broken_even ? "var(--color-success)" : "var(--color-text)",
            }}>
              {breakEven.broken_even
                ? `Yes — ${money(breakEven.surplus)} ahead`
                : `Not yet — ${money(breakEven.remaining)} to go`}
            </div>
            <p style={{ margin: "var(--space-2) 0 0", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              {/* ALL TIME, SAID OUT LOUD, because the year selector is
                  right above it and would otherwise imply this figure
                  moved with it. */}
              All time: {money(breakEven.recovered)} earned back of {money(breakEven.invested)} spent
              {breakEven.recovered_pct == null ? "" : ` (${breakEven.recovered_pct}%)`}.
              Earned back is money <strong>received</strong>, not invoiced.
            </p>
          </Card>
        )}

        {/*
          * TWO CHARTS, BECAUSE THERE ARE TWO QUESTIONS (#437).
          *
          * Asked for: "Maybe a graph with the income and expenses ... so
          * it's easy to tell if your expenses are more than your income
          * and a separate graph that specifically graphs profit each
          * season so you can use it to trend your profits over time."
          *
          * Right, and one chart answered neither. Invoiced beside
          * Received beside Expenses plots two measures of the same money
          * next to a third thing, and a reader looking for "am I
          * underwater" has to do the comparison themselves.
          *
          *   1. Am I underwater?      income against expenses, per season
          *   2. Is it getting better? profit across seasons, as a trend
          */}
        <Card title="Income against expenses" className="section-spacing">
          {!pnl ? <Spinner /> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="season" />
                <YAxis />
                <Tooltip />
                <Legend />
                {/*
                  * RECEIVED, NOT INVOICED. The footer states the basis --
                  * profit is received less expenses -- so comparing
                  * billed-but-unpaid against expenses would draw a
                  * picture the profit figure disagrees with. Invoiced
                  * keeps its column in the table, where Outstanding
                  * already explains the gap.
                  *
                  * GROUPED, NOT OVERLAPPING, which was asked for as
                  * overlapping and is worth the disagreement: when the
                  * two are close the eye reads the visible sliver AS the
                  * difference, and it is not. Grouped puts both at a
                  * shared baseline, where "taller" is unambiguous -- and
                  * the moment the difference goes the wrong way is the
                  * whole reason to look.
                  */}
                {/* STACKED, so the two together stand for invoiced. Expenses
                    stays a SEPARATE grouped bar -- the comparison this chart
                    exists for is income against expenses, and folding
                    expenses into the stack would destroy it. */}
                <Bar dataKey="received" stackId="income" fill={RECEIVED} name="Income received" />
                {/* A TINT RATHER THAN fill="none" (#476).
                  *
                  * Recharts draws each legend swatch from the series'
                  * FILL, so "none" rendered the words "Invoiced, not yet
                  * paid" with nothing beside them -- while the other two
                  * entries had boxes. Two of three marked reads as a
                  * rendering fault, not a style.
                  *
                  * It is the entry that most needs a marker, because the
                  * thing it names is the hardest to see: a thin band
                  * stacked above the filled bar, and absent entirely in
                  * a season where everything was paid. A reader who
                  * cannot match the words to a cue is left guessing
                  * which part of a two-tone bar is which, which defeats
                  * #452's stacking.
                  *
                  * The tint is faint enough that the segment still reads
                  * as UNFILLED against the solid received bar below it,
                  * which was the point of the outline. */}
                {/* THE SWATCH SAYS "OUTLINE", NOT "PALER BLUE" (#476
                  * follow-up).
                  *
                  * Reported from testing: "the legend shows the marker
                  * beside the 'invoiced, not yet paid' but it's just
                  * showing as a lighter shade of blue instead of the
                  * transparent blue. It's probably the border on the
                  * marker is to thick so you can't tell it's transparent
                  * blue interior."
                  *
                  * Right about the cause. Recharts derives the legend
                  * icon from the series' fill and fillOpacity, and draws
                  * it at roughly 14px. A 2px stroke on a 14px box leaves
                  * very little interior, so a 25% tint inside a solid
                  * border averages out to one flat lighter blue -- while
                  * the BAR, which is tens of pixels wide, reads exactly
                  * as intended. Same values, two sizes, two different
                  * impressions.
                  *
                  * legendType="line" asks for a stroke-drawn icon
                  * instead of a filled box: the swatch becomes a line in
                  * the series' own colour, which matches the band's
                  * OUTLINE -- the thing that distinguishes it -- rather
                  * than trying to reproduce a fill-and-border at a size
                  * too small to hold both.
                  *
                  * Not solved by thinning the stroke: 1px would read as
                  * a hairline on the chart and lose the outline that
                  * #452's stacking depends on. The chart is the thing
                  * that has to be right; the legend only has to be
                  * matchable to it. */}
                <Bar dataKey="unpaid" stackId="income"
                     fill={INVOICED} fillOpacity={0.25}
                     legendType="line"
                     stroke={INVOICED} strokeWidth={2} name="Invoiced, not yet paid" />
                <Bar dataKey="expenses" fill={EXPENSES} name="Expenses" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Profit by season" className="section-spacing">
          {!pnl ? <Spinner /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={pnl}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="season" />
                <YAxis />
                <Tooltip />
                {/* THE ZERO LINE, DRAWN EXPLICITLY. Crossing it is the
                    event this chart exists to show, and a gridline that
                    happens to land near zero is not the same as one that
                    IS zero. */}
                <ReferenceLine y={0} stroke="var(--color-text-muted)" strokeWidth={1} />
                {/*
                  * A LINE, not bars: this is a series over time and the
                  * reason to look at it is DIRECTION.
                  *
                  * The same blue as Income received, and deliberately no
                  * fourth colour. Profit IS received less expenses, so
                  * sharing its hue says where it comes from -- and a new
                  * series would need the palette re-validated for
                  * colour-vision separation, which the existing three
                  * were (dE 15.9 worst pair, after the shipped green/red
                  * pairing turned out to separate by only 6.3 under
                  * deuteranopia).
                  */}
                <Line type="monotone" dataKey="profit" stroke={RECEIVED}
                      strokeWidth={2} dot name="Profit" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* THE NUMBERS, IN TEXT (#431).
            Asked for as "both labelled as real columns" -- and it is also
            what makes the chart's dark-mode contrast acceptable, and what
            gives the outstanding figure somewhere to live. A bar chart
            cannot show "invoiced minus received" without inventing a
            fourth bar for a difference. */}
        <Card title="By season" className="section-spacing">
          {!pnl ? <Spinner /> : (
            <DataTable
              rows={pnl}
              rowKey={(r) => `${r.year}-${r.season}`}
              emptyTitle="Nothing recorded yet"
              emptyDescription="Sales and expenses appear here once you have some."
              columns={[
                { key: "season", header: "Season", render: (r) => `${r.season} ${r.year}` },
                { key: "invoiced", header: "Invoiced", render: (r) => money(r.invoiced) },
                { key: "received", header: "Received", render: (r) => money(r.received) },
                {
                  key: "outstanding",
                  header: "Outstanding",
                  /* Not an error. Consignment honey sits in a shop unsold
                     (#226), so a gap is the normal state of this business.
                     Negative means payments landed against an earlier
                     season's invoices, which is equally ordinary. */
                  render: (r) => (r.outstanding
                    ? <span style={{ color: "var(--color-text-muted)" }}>{money(r.outstanding)}</span>
                    : "—"),
                },
                { key: "expenses", header: "Expenses", render: (r) => money(r.expenses) },
                {
                  key: "profit",
                  header: "Profit",
                  /* CASH BASIS, said out loud in the caption below rather
                     than left for the reader to infer from two incomes. */
                  render: (r) => money(r.profit),
                },
              ]}
            />
          )}
          <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", marginBottom: 0 }}>
            Profit is <strong>received less expenses</strong> — the cash that reached you.
            Outstanding is honey invoiced but not yet paid for.
          </p>
        </Card>
      </LockedFeature>
      )}
    </div>
  );
}
